import dayjs from 'dayjs';
import type { Stages } from '../../api/process-client/process-client.type';
import { useProcessStoreUpdate } from '../process-store/process-store';
import type { EventScope, ProcessScope, ProcessWorkflow } from '../process-store/process-store.type';
/**
 * Custom hook to easily access and update the process store
 * It mimics the same API as the ProcessContext for easier migration
 */
const useProcessStore = () => {
  const {
    scope,
    setScope,
    stages,
    setStages,
    workflow,
    setWorkflow,
    eventScope,
    setEventScope,
    isComparisonView,
    setIsComparisonView,
    rightScope,
    setRightScope,
    rightStages,
    setRightStages,
    rightWorkflow,
    setRightWorkflow,
    rightEventScope,
    setRightEventScope,
  } = useProcessStoreUpdate();

  // We return the same interface as ProcessContext to make migration easier
  return {
    scope,
    setScope,
    stages,
    setStages,
    workflow,
    setWorkflow,
    eventScope,
    setEventScope,
    isComparisonView,
    setIsComparisonView,
    rightScope,
    setRightScope,
    rightStages,
    setRightStages,
    rightWorkflow,
    setRightWorkflow,
    rightEventScope,
    setRightEventScope,
  };
};

/**
 * Select specific parts of the process store with proper memoization
 * This helps prevent unnecessary re-renders
 */
const useProcessSelector = <T>(
  selector: (state: {
    scope: ProcessScope;
    stages: Stages[];
    workflow: ProcessWorkflow | null;
    eventScope: EventScope;
    isComparisonView: boolean;
    rightScope: ProcessScope;
    rightStages: Stages[];
    rightWorkflow: ProcessWorkflow | null;
    rightEventScope: EventScope;
  }) => T,
): T => {
  return useProcessStoreUpdate(selector);
};

/**
 * Resets the process store to its default state
 *
 * This function:
 * - Clears the cached process store from localStorage
 * - Resets all state values to their defaults for both primary and comparison views
 * - Resets dates to default range (current month to 1 month prior)
 * - Clears all selections including portfolio, team, board, workflow, and event scopes
 *
 * Used when switching portfolios or when a complete reset of the process view is needed.
 */

const resetProcessStore = () => {
  const store = useProcessStoreUpdate.getState();

  // Remove the cached process store from localStorage
  localStorage.removeItem('process-storage');
  localStorage.removeItem('process-scope');

  // Reset all state in the store to defaults
  store.setScope({
    portfolio: null,
    team: null,
    board: null,
    epic: null,
    workflow: null,
    startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
    endDate: dayjs().toISOString(),
    taskType: null,
    useWorkflow: false,
    useGroups: true,
  });

  store.setStages([]);
  store.setWorkflow(null);
  store.setEventScope({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  // Reset right side store too
  store.setRightScope({
    portfolio: null,
    team: null,
    board: null,
    epic: null,
    workflow: null,
    startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
    endDate: dayjs().toISOString(),
    taskType: null,
    useWorkflow: false,
    useGroups: true,
  });

  store.setRightStages([]);
  store.setRightWorkflow(null);
  store.setRightEventScope({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  store.setIsComparisonView(false);
};

export { resetProcessStore, useProcessSelector, useProcessStore };
